/**
 * Routing Tab - Inbound Routing Rules
 *
 * Manages inbound routing rules that determine which PROJECT
 * incoming records are routed to based on CRM entity data.
 */

import { useState, useCallback, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, GripVertical, Power, Loader2, Info } from 'lucide-react';
import { RoutingRuleSheet } from '@/features/routing/components';
import { defaultProjectState } from '@/features/routing/utils/default-project';
import { SchemeLinkBanner } from '@/features/analysis-setup';
import { TableSkeleton } from '@/components/ui/skeletons';
import {
  useInboundRoutingRules,
  useDeleteInboundRoutingRule,
  useToggleInboundRoutingRule,
  useReorderInboundRoutingRules,
  useInboundRoutingFallback,
  useUpdateInboundRoutingFallback,
} from '@/features/routing/hooks';
import type { InboundRoutingRule } from '@/lib/api/types';

interface RoutingTabProps {
  projectId: string;
}

export function RoutingTab({ projectId }: RoutingTabProps) {
  const { t } = useTranslation(['settings', 'common']);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<InboundRoutingRule | null>(null);

  // Data fetching
  const { data: rulesResponse, isLoading: rulesLoading } = useInboundRoutingRules({
    projectId,
  });
  const { data: fallbackSettings, isLoading: fallbackLoading } =
    useInboundRoutingFallback();

  const deleteRule = useDeleteInboundRoutingRule();
  const toggleRule = useToggleInboundRoutingRule();
  const reorderRules = useReorderInboundRoutingRules();
  const updateFallback = useUpdateInboundRoutingFallback();

  const rules = useMemo(() => rulesResponse?.data || [], [rulesResponse?.data]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder rules
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      // One reorder at a time: a second one planned before the first is
      // written is refused as a conflict (#2874).
      if (reorderRules.isPending) return;

      if (over && active.id !== over.id) {
        const oldIndex = rules.findIndex((r) => r.id === active.id);
        const newIndex = rules.findIndex((r) => r.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          // Calculate new order
          const reorderedRules = arrayMove(rules, oldIndex, newIndex);
          const ruleIds = reorderedRules.map((r: InboundRoutingRule) => r.id);

          // Call API to persist new order
          reorderRules.mutate({ ruleIds });
        }
      }
    },
    [rules, reorderRules]
  );
  const isLoading = rulesLoading || fallbackLoading;

  const handleOpenCreate = useCallback(() => {
    setEditingRule(null);
    setIsFormOpen(true);
  }, []);

  const handleOpenEdit = useCallback((rule: InboundRoutingRule) => {
    setEditingRule(rule);
    setIsFormOpen(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingRule(null);
  }, []);

  const handleToggle = useCallback(
    (id: string) => {
      toggleRule.mutate(id);
    },
    [toggleRule]
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (
        window.confirm(
          t('routing.deleteConfirm', 'Are you sure you want to delete this rule?')
        )
      ) {
        deleteRule.mutate(id);
      }
    },
    [deleteRule, t]
  );

  const handleToggleDefault = useCallback(() => {
    const isCurrentlyDefault = fallbackSettings?.defaultProjectId === projectId;
    updateFallback.mutate({
      defaultProjectId: isCurrentlyDefault ? null : projectId,
    });
  }, [updateFallback, projectId, fallbackSettings?.defaultProjectId]);

  if (isLoading) {
    return <TableSkeleton rows={5} />;
  }

  const isDefaultProject = fallbackSettings?.defaultProjectId === projectId;
  const rejectUnmatchedRecords = fallbackSettings?.rejectUnmatchedRecords ?? false;
  // Any default project — this one, another, or one outside the reader's
  // access (its id masked, #2870) — takes unmatched records before rejection
  // applies.
  const { isSet: hasDefaultProject, isHidden: defaultProjectHidden } =
    defaultProjectState(fallbackSettings);
  const rejectIsStandby = rejectUnmatchedRecords && hasDefaultProject;

  return (
    <div className="space-y-6">
      <SchemeLinkBanner step="routing" />

      {/* Header */}
      <div className="settings-section">
        <h3 className="text-base font-medium text-fg mb-2">
          {t('routing.title', 'Inbound Routing')}
        </h3>
        <p className="text-sm text-fg-muted">
          {t(
            'routing.description',
            'Configure rules to determine when incoming records should be routed to this project based on CRM data.'
          )}
        </p>
      </div>

      {/* Global order note */}
      <div className="p-3 rounded-lg border border-border bg-bg-muted flex items-start gap-2">
        <Info className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
        <p className="text-xs text-fg-muted">
          {t(
            'routing.globalOrderNote',
            'Drag rules to reorder within this project. To see and change the order of rules across all projects, go to'
          )}{' '}
          <Link to="/settings" className="text-primary hover:underline">
            {t('routing.globalOrderNoteLink', 'Settings → Routing')}
          </Link>
        </p>
      </div>

      {/* Default Project */}
      <div className="border-t border-border pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-fg">
              {t('routing.fallbackTitle', 'Default Project')}
            </h3>
            <p className="text-xs text-fg-muted mt-0.5">
              {t(
                'routing.fallbackDescription',
                'Records that do not match any routing rules will be sent to the default project.'
              )}
            </p>
            {defaultProjectHidden && (
              <p className="text-xs text-fg-muted mt-1">
                {t(
                  'routing.defaultProjectHidden',
                  'A default project is set, but it is outside your access. Records that match no rule go to it.'
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isDefaultProject && (
              <Badge variant="success">{t('routing.isDefault', 'Default')}</Badge>
            )}
            <Button
              variant={isDefaultProject ? 'outline' : 'default'}
              size="sm"
              onClick={handleToggleDefault}
              disabled={updateFallback.isPending}
            >
              {updateFallback.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {isDefaultProject
                ? t('routing.removeDefault', 'Remove Default')
                : t('routing.setAsDefault', 'Set as Default')}
            </Button>
          </div>
        </div>

        <div
          className={`flex items-start justify-between gap-4 rounded-lg border p-3 transition-colors ${
            rejectUnmatchedRecords
              ? 'border-danger bg-bg-muted'
              : 'border-border bg-bg-surface'
          }`}
        >
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-fg">
                {t('routing.rejectUnmatchedTitle', 'Reject Unmatched Records')}
              </span>
              {rejectUnmatchedRecords && (
                <Badge variant="danger" size="sm">
                  {rejectIsStandby
                    ? t('routing.rejectUnmatchedStandby', 'Standby')
                    : t('routing.rejectUnmatchedActive', 'Rejecting')}
                </Badge>
              )}
            </div>
            <p className="text-xs text-fg-muted">
              {t(
                'routing.rejectUnmatchedDescription',
                'Ignore records that do not match any routing rule.'
              )}
            </p>
            <p
              className={`text-xs ${
                rejectUnmatchedRecords ? 'text-danger' : 'text-fg-muted'
              }`}
            >
              {hasDefaultProject
                ? t(
                    'routing.rejectUnmatchedWithDefault',
                    'A default project is configured, so it still wins first. Rejection only applies after the default project is removed.'
                  )
                : t(
                    'routing.rejectUnmatchedWithoutDefault',
                    'When enabled without a default project, unmatched records are filtered out instead of being routed.'
                  )}
            </p>
          </div>
          <Switch
            size="sm"
            error={rejectUnmatchedRecords}
            checked={rejectUnmatchedRecords}
            disabled={updateFallback.isPending}
            onCheckedChange={(checked) =>
              updateFallback.mutate({ rejectUnmatchedRecords: checked })
            }
          />
        </div>
      </div>

      {/* Rules List */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-fg">
            {t('routing.rulesTitle', 'Routing Rules')}
          </h3>
          <Button size="sm" onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            {t('routing.addRule', 'Add Rule')}
          </Button>
        </div>

        {rules.length === 0 ? (
          <div className="rounded-lg border border-border bg-bg-muted p-6 text-center">
            <p className="text-sm text-fg-muted mb-4">
              {t(
                'routing.emptyState',
                'No routing rules configured for this project. Records will only be routed here if this is the default project.'
              )}
            </p>
            <Button variant="outline" size="sm" onClick={handleOpenCreate}>
              <Plus className="h-4 w-4 mr-2" />
              {t('routing.createFirstRule', 'Create First Rule')}
            </Button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={rules.map((r) => r.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {rules.map((rule) => (
                  <SortableRuleCard
                    key={rule.id}
                    rule={rule}
                    onEdit={() => handleOpenEdit(rule)}
                    onDelete={() => handleDelete(rule.id)}
                    onToggle={() => handleToggle(rule.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Info Box */}
      <p className="text-xs text-fg-muted">
        <span className="font-medium">
          {t('routing.tipsTitle', 'How Routing Works')}:{' '}
        </span>
        {t(
          'routing.tipsDescription',
          'Rules are evaluated in priority order. The first matching rule determines which project receives the record. Use CRM fields like crm.ASSIGNED_BY_ID, crm.STAGE_ID, or crm.CATEGORY_ID to route records.'
        )}
      </p>

      {/* Create/Edit Form Sheet — shared with the analysis-setup hub, which opens
          the same sheet per project card instead of navigating here. */}
      <RoutingRuleSheet
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        projectId={projectId}
        rule={editingRule}
        onSaved={handleCloseForm}
      />
    </div>
  );
}

// ============================================================================
// Sortable Rule Card Component (with drag-and-drop)
// ============================================================================

interface SortableRuleCardProps {
  rule: InboundRoutingRule;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
}

function SortableRuleCard({ rule, onEdit, onDelete, onToggle }: SortableRuleCardProps) {
  const { t } = useTranslation(['settings', 'common']);
  const hasConditions = rule.conditions && Object.keys(rule.conditions).length > 0;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-start gap-3 p-4 rounded-lg border
        ${rule.isEnabled ? 'border-border bg-bg-elevated' : 'border-border/50 bg-bg-muted opacity-60'}
        ${isDragging ? 'shadow-lg' : ''}
      `}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className="text-fg-muted cursor-grab active:cursor-grabbing touch-none select-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm font-medium text-fg truncate">{rule.name}</h4>
          <Badge variant={rule.isEnabled ? 'success' : 'default'}>
            {rule.isEnabled
              ? t('routing.enabled', 'Enabled')
              : t('routing.disabled', 'Disabled')}
          </Badge>
          <span className="text-xs text-fg-muted">
            {t('routing.priority', 'Priority')}: {rule.priority}
          </span>
        </div>

        {rule.description && (
          <p className="text-sm text-fg-muted mb-2">{rule.description}</p>
        )}

        {hasConditions ? (
          <div className="text-xs text-fg-muted bg-bg-surface rounded px-2 py-1 font-mono">
            {JSON.stringify(rule.conditions, null, 0).slice(0, 100)}
            {JSON.stringify(rule.conditions).length > 100 && '...'}
          </div>
        ) : (
          <p className="text-xs text-fg-muted italic">
            {t('routing.noConditions', 'No conditions defined')}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-md"
          onClick={onToggle}
          title={
            rule.isEnabled
              ? t('routing.disable', 'Disable')
              : t('routing.enable', 'Enable')
          }
        >
          <Power
            className={`h-4 w-4 ${rule.isEnabled ? 'text-success' : 'text-fg-muted'}`}
          />
        </Button>
        <Button
          variant="ghost"
          size="icon-md"
          onClick={onEdit}
          title={t('common:common.edit', 'Edit')}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon-md"
          onClick={onDelete}
          className="text-danger hover:text-danger"
          title={t('common:common.delete', 'Delete')}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
